import { LocationSchema } from '../schema/location_schema.js';
import { ReviewSchema } from '../schema/review_schema.js';
import { ServiceSchema } from '../schema/service_schema.js';
import { ServiceBase } from './service_base.js';

class LocationService extends ServiceBase {
  getLocations(data /* { north, east, south, west } */) {
    return this.locationRequest('get-locations', data, []);
  }

  getLocationsByIds(list) {
    return this.locationRequest('get-locations-by-ids', { list }, []);
  }

  async getLocationInfo(data) {
    let details = await this.locationRequest('get-location-details', data);
    return details || { notFound: true };
  }

  getLocationArea(id) {
    return this.locationRequest(
      'get-location-area',
      {
        id,
      },
      {}
    );
  }

  getLocationRelations(id) {
    return this.locationRequest(
      'get-location-relations',
      {
        id,
      },
      []
    );
  }

  getAvailability(id) {
    return this.locationRequest(
      'get-location-availability',
      {
        id,
      },
      {}
    );
  }

  async getLocationHistory(id) {
    return this.locationRequest(
      'get-location-history',
      {
        id,
      },
      []
    );
  }

  searchLocations(
    data /* { name, type, north, west, south, east, provider } */
  ) {
    return this.locationRequest('search-locations', data, []);
  }

  async addLocation(data) {
    let idobj = await this.locationRequest('add-location', data);
    return (idobj && idobj.id) || 0;
  }

  async changeLocation(data) {
    let idobj = await this.locationRequest('change-location', data);
    return (idobj && idobj.id) || 0;
  }

  async removeLocation(id) {
    let idobj = await this.locationRequest('remove-location', {
      id,
    });
    return (idobj && idobj.id) || 0;
  }

  uploadPicture(pic, info) {
    return this.serviceRequest(
      'upload-pic',
      {
        pic,
        info,
      },
      null
    );
  }

  setPictureDescription(picId, description) {
    return this.serviceRequest('set-pic-description', {
      id: picId,
      description,
    });
  }

  getPicture(picid, size) {
    return this.serviceRequest(
      'get-pic',
      {
        picid,
        size,
      },
      null
    );
  }

  getPictures(data /* { north, east, south, west } */) {
    return this.serviceRequest('get-pics', data, []);
  }

  associate(data /* { loc, relations } */) {
    return this.locationRequest('associate', data);
  }

  associateWith(data) {
    return this.locationRequest('associate-with', data);
  }

  findByCriteria(data) {
    return this.locationRequest('find-by-criteria', data, []);
  }

  setDataProvider(data /* { loc, type, provider, id } */) {
    return this.locationRequest('set-dataprovider', data);
  }

  async queryDataProvider(data /* { loc, type } */) {
    return this.locationRequest('query-dataprovider', data);
  }

  findByDataProvider(data /* { provider, type, id } */) {
    return this.locationRequest('find-by-dataprovider', data);
  }

  async addReview(data) {
    let idobj = await this.reviewRequest('add-review', data);
    return idobj?.id ?? 0;
  }

  changeReview(data) {
    return this.reviewRequest('change-review', data);
  }

  deleteReview(reviewId) {
    return this.reviewRequest('delete-review', {
      id: reviewId,
    });
  }

  getReviews(locId) {
    return this.reviewRequest('get-reviews', {
      locId,
    });
  }

  getRatings(locId) {
    return this.reviewRequest('get-ratings', {
      locId,
    });
  }

  getDefaultLocation() {
    return this.serviceRequest('get-default-location');
  }

  reverseGeocode(data) {
    return this.serviceRequest('reverse-geocode', data);
  }

  getSchema(data) {
    switch (data.module) {
      case 'location':
        return LocationSchema[data.action];
      case 'review':
        return ReviewSchema[data.action];
      case 'service':
        return ServiceSchema[data.action];
    }
  }
}

export { LocationService };
